import { ActionSheetProvider } from '@expo/react-native-action-sheet'
import { setDateLocale } from '@nextforce/api'
import NetInfo from '@react-native-community/netinfo'
import { Text, ThemeProvider } from '@rneui/themed'
import * as Sentry from '@sentry/react-native'
import { focusManager, onlineManager } from '@tanstack/react-query'
import { AuthenticationProvider } from 'authentication/context'
import 'expo-dev-client'
import { getLocales } from 'expo-localization'
import * as Updates from 'expo-updates'
import React, { useEffect, useState } from 'react'
import { AppState, AppStateStatus, LogBox, Platform, View } from 'react-native'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { KeyboardProvider } from 'react-native-keyboard-controller'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { initializeSslPinning } from 'react-native-ssl-public-key-pinning'
import { ToastProvider } from 'react-native-toast-notifications'
import { ThemeChangeProvider } from 'settings/change_theme'
import Loading from 'shared/components/loading'
import CustomToast from 'shared/components/toast'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import { DefaultThemeContainer } from 'shared/default_theme_container'
import JailMonkey from 'shared/wrappers/jail-monkey'
import { AnalyticsProvider } from './analytics/context'
import i18n, { t } from './i18l'
import { Navigation } from './navigation'
import { CustomPersistQueryClientProvider } from './query_client_context'
import { startSentry } from './sentry'
import { ServerProvider } from './server/context'
import { appTheme, useThemeFonts } from './theme'

startSentry()

function App() {
  const [fontsLoaded] = useThemeFonts()
  const [isRooted, setRootedDevice] = useState(false)

  configureLocale()
  useEffect(configureLogBox, [])
  useEffect(configureFocusManager, [])

  // Check for rooted Device
  useEffect(() => {
    try {
      setRootedDevice(JailMonkey.isJailBroken())
    } catch (e) {
      console.warn(e)
    }
  }, [])

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const disableSslPinning = process.env.EXPO_PUBLIC_DISABLE_SSL === 'true'
      if (!disableSslPinning) {
        initializeSslPinning({
          'nextforce.us': {
            includeSubdomains: true,
            publicKeyHashes: [
              '++MBgDH5WGvL9Bcn5Be30cRcL0f5O+NyoXuWtQdX1aI=',
              'f0KW/FtqTjs108NpYj42SrGvOB2PpxIVM8nWxjPqJGE=',
              'NqvDJlas/GRcYbcWE8S/IceH9cq77kg0jVhZeAPXq8k=',
              '9+ze1cZgR9KO1kZrVDxA4HQ6voHRCSVNz4RdTCx4U8U=',
              'KwccWaCgrnaw6tsrrSO61FgLacNgG2MMLq8GE6+oP5I=',
              '18tkPyr2nckv4fgo0dhAkaUtJ2hu2831xlO2SKhq8dg=',
              'QpddHFeJdTbbu4ltNiOIOUM/pLIdA+FnQ4l+ZQJE7LA=',
            ],
          },
        }).catch(e => console.log(e))
      }
    }
  }, [])

  useEffect(() => {
    return NetInfo.addEventListener(state => {
      const status =
        state.isConnected !== null &&
        state.isConnected &&
        Boolean(state.isInternetReachable)
      onlineManager.setOnline(status)
    })
  }, [])

  if (!fontsLoaded) {
    return (
      <DefaultThemeContainer>
        <Loading text="Loading Fonts..." />
      </DefaultThemeContainer>
    )
  }

  if (isRooted) {
    return (
      <DefaultThemeContainer>
        <Text
          style={{
            flex: 1,
            paddingTop: 200,
            alignSelf: 'center',
            fontSize: 24,
          }}
        >
          {t('rootedDeviceNotSupported')}
        </Text>
      </DefaultThemeContainer>
    )
  }

  return (
    <KeyboardProvider>
      <SafeAreaProvider>
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'center',
            backgroundColor: Platform.OS === 'web' ? '#353B4A' : 'white',
          }}
        >
          <View
            style={
              Platform.OS === 'web'
                ? {
                    flex: 1,
                    maxWidth: WEB_SCREEN_WIDTH,
                  }
                : { flex: 1 }
            }
          >
            <GestureHandlerRootView style={{ flex: 1 }}>
              <ServerProvider>
                <ActionSheetProvider>
                  <CustomPersistQueryClientProvider>
                    <ThemeProvider theme={appTheme}>
                      <ThemeChangeProvider>
                        <AnalyticsProvider>
                          <AuthenticationProvider>
                            <ToastProvider renderToast={CustomToast}>
                              <Navigation />
                            </ToastProvider>
                          </AuthenticationProvider>
                        </AnalyticsProvider>
                      </ThemeChangeProvider>
                    </ThemeProvider>
                  </CustomPersistQueryClientProvider>
                </ActionSheetProvider>
              </ServerProvider>
            </GestureHandlerRootView>
          </View>
        </View>
      </SafeAreaProvider>
    </KeyboardProvider>
  )
}

export default Sentry.wrap(App)

function configureLocale() {
  const locales = getLocales()
  let deviceLocale: string = 'en'

  if (locales.length > 0 && locales[0] && locales[0].languageCode) {
    deviceLocale = locales[0].languageCode
  }

  let appLocale = 'en'

  if (deviceLocale.startsWith('en') || deviceLocale.startsWith('es')) {
    appLocale = deviceLocale
  }

  i18n.locale = appLocale
  setDateLocale(appLocale)
}

function configureFocusManager() {
  focusManager.setEventListener(handleFocus => {
    const listener = (status: AppStateStatus) =>
      handleFocus(status === 'active')
    const subscription = AppState.addEventListener('change', listener)

    return () => {
      subscription.remove()
    }
  })
}

function configureUpdates() {
  const subscription = Updates.addUpdatesStateChangeListener(event => {
    if (event.context.isUpdateAvailable) {
      alert(t('updateMessage'))
    }
  })

  return () => subscription.remove()
}

// function configureSplashScreen() {
//   async function prepare() {
//     try {
//       await SplashScreen.preventAutoHideAsync()
//     } catch (e) {
//       console.warn(e)
//     }
//   }
//   prepare().catch(null)
// }

function configureLogBox() {
  if (LogBox) {
    // https://github.com/tannerlinsley/react-query/discussions/356
    // https://github.com/facebook/react-native/issues/28558#issuecomment-722334820
    LogBox.ignoreLogs([
      'Setting a timer',
      'Animated: `useNativeDriver`',
      'componentWillReceiveProps',
      'Request failed with status code 504',
    ])
  }
}
